import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../api/passwordReset';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await forgotPassword({ email });
      setMessage(response.message || 'If an account exists with this email, a password reset link has been sent');
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
        <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/3 rounded-full blur-3xl" />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 mb-4 shadow-lg shadow-emerald-500/20">
              <svg className="w-8 h-8 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
              Check Your Email
            </h1>
          </div>

          <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
            <div className="alert-success text-center mb-6">
              <p className="font-medium">{message}</p>
              <p className="mt-2 text-sm opacity-80">
                Please check your email inbox and follow the instructions to reset your password.
                The link will expire in 1 hour.
              </p>
            </div>

            <Link
              to="/login"
              className="btn-primary w-full py-3.5 rounded-xl text-base block text-center"
            >
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
      <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />
      <div className="absolute top-20 left-20 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400/3 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20">
            <svg className="w-8 h-8 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
            Forgot Password
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
          {error && (
            <div className="alert-error text-center mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="opacity-0 animate-slide-up stagger-3">
              <label htmlFor="email" className="block text-sm font-medium text-zinc-400 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                disabled={loading}
                className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100 disabled:opacity-50"
              />
            </div>

            <div className="opacity-0 animate-slide-up stagger-4 pt-2">
              <button
                type="submit"
                className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={loading}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>

            <p className="text-center text-zinc-500 text-sm pt-2 opacity-0 animate-fade-in stagger-5">
              <Link to="/login" className="text-amber-400 hover:text-amber-300 hover:underline">
                Back to Login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
